import {
  CheckCircleOutlined,
  QuestionCircleOutlined,
  RightCircleOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import {
  PracticeTimesSchedule,
  PracticeTimesScheduleEntry,
} from '@hefe/models';
import {
  BasketballPracticeTimesApi,
  BasketballPracticeTimesSlice,
  BasketballTeamsSlice,
  currentUserSelector,
  GymsSlice,
  ModalsSlice,
  updateUserSettings,
} from '@hefe/store';
import { PracticeTimesScheduleOverview } from '@hefe/ui';
import { formatDate } from '@hefe/utils';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Row,
  Select,
  Space,
  Spin,
  Tabs,
  Tour,
  TourProps,
  Typography,
} from 'antd';
import { produce } from 'immer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import PracticeTimesSchedulesComponent from '../../components/practice-times-schedules/practice-times-schedules.component';
import SyncPracticesButton from '../../components/sync-practices-button/sync-practices-button.component';
import AddEditPracticeTimesScheduleEntryForm from '../../forms/add-edit-practice-time-draft-time/add-edit-practice-time-draft-time.form';
import styles from './practices-admin.module.scss';

export default function PracticesAdminPage() {
  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);

  const [deleteDraftTime, { isLoading: isDeletingDraftTime }] =
    BasketballPracticeTimesApi.useDeletePracticeTimesScheduleEntryMutation();

  const { data: upcomingSchedules, isFetching: isFetchingUpcomingSchedules } =
    BasketballPracticeTimesApi.useFetchUpcomingPracticeTimesSchedulesQuery();

  const { data: schedules, isLoading: isLoadingSchedules } =
    BasketballPracticeTimesApi.useFetchPracticeTimesSchedulesQuery();

  const activeScheduleId = useSelector(
    BasketballPracticeTimesSlice.activeScheduleIdSelector
  );

  const { data: scheduleData, isFetching: isLoadingSchedule } =
    BasketballPracticeTimesApi.useFetchBasketballPracticeTimesDraftOverviewQuery(
      activeScheduleId ?? 0,
      {
        skip: !activeScheduleId || activeScheduleId === 0,
      }
    );

  const [tourOpen, setTourOpen] = useState(false);

  // refs for tour
  const refTabSchedules = useRef(null);
  const refCurrentActivePlan = useRef(null);
  const refTabPracticeTimes = useRef(null);
  const refDropdownPlans = useRef(null);
  const refSyncButton = useRef(null);

  useEffect(() => {
    if (
      !user?.user_settings?.dismissed_content?.tours?.includes(
        'practices_admin'
      )
    ) {
      setTourOpen(true);
    }
  }, [user]);

  useEffect(() => {
    fetchGyms();
    fetchTeamsSelect();
  }, []);

  const fetchGyms = () => {
    dispatch(GymsSlice.fetchGyms({ cacheDuration: 5 * 60 * 1000 }));
  };

  const fetchTeamsSelect = () => {
    dispatch(
      BasketballTeamsSlice.fetchBasketballTeamsSelect({
        cacheDuration: 5 * 60 * 1000,
      })
    )
      .unwrap()
      .catch(() => null);
  };

  const markTourAsRead = () => {
    if (
      !user?.user_settings?.dismissed_content?.tours ||
      user?.user_settings?.dismissed_content?.tours.includes('practices_admin')
    ) {
      return;
    }
    const settings = produce(user.user_settings, (draft) => {
      if (draft?.dismissed_content?.tours) {
        draft.dismissed_content.tours.push('practices_admin');
      }
    });

    dispatch(updateUserSettings(settings))
      .unwrap()
      .catch(() => null);
  };

  const currentSchedule = useMemo(() => {
    return upcomingSchedules?.[0];
  }, [upcomingSchedules]);

  const upcomingSchedule = useMemo(() => {
    return upcomingSchedules?.[1];
  }, [upcomingSchedules]);

  const additionalSchedules = useMemo(() => {
    return upcomingSchedules?.slice(2);
  }, [upcomingSchedules]);

  const schedulesSelect = useMemo(() => {
    const returnValue = schedules?.map((schedule) => ({
      label: schedule.name,
      value: schedule.id,
    }));

    return returnValue;
  }, [schedules]);

  const openPracticeTimeSettings = (
    teamId: number,
    practiceTime: PracticeTimesScheduleEntry | undefined,
    dayOfWeek: number
  ) => {
    const entry =
      practiceTime ??
      ({
        weekday: dayOfWeek,
        basketball_teams: [
          {
            id: teamId,
            name: scheduleData?.teams?.find((team) => team.id === teamId)?.name,
          },
        ],
      } as PracticeTimesScheduleEntry);

    dispatch(
      ModalsSlice.modalsActions.openModal({
        component: (
          <AddEditPracticeTimesScheduleEntryForm
            teamId={teamId}
            entry={entry}
            weekday={dayOfWeek}
            scheduleId={scheduleData?.id ?? 0}
          />
        ),
        title: t('practice_time.edit'),
        formId: `add-edit-team-practice-times-schedule-entry-form-${
          entry.id ?? 'undefined'
        }`,
      })
    );
  };

  const scheduleDescriptionText = (schedule?: PracticeTimesSchedule) => {
    if (!schedule) {
      return '';
    }

    if (schedule.from && schedule.till) {
      return t(
        'practices.administration.practice_times.schedules.active_from_till.from_till',
        { from: formatDate(schedule.from), till: formatDate(schedule.till) }
      );
    } else if (schedule.from) {
      return t(
        'practices.administration.practice_times.schedules.active_from_till.from',
        { from: formatDate(schedule.from) }
      );
    } else if (schedule.till) {
      return t(
        'practices.administration.practice_times.schedules.active_from_till.till',
        { till: formatDate(schedule.till) }
      );
    }

    return '';
  };

  const tourSteps: TourProps['steps'] = [
    {
      title: t('practices.administration.tour.one.title'),
      description: t('practices.administration.tour.one.description'),
      target: () => refTabSchedules.current,
    },
    {
      title: t('practices.administration.tour.two.title'),
      description: t('practices.administration.tour.two.description'),
      target: () => refCurrentActivePlan.current,
    },
    {
      title: t('practices.administration.tour.three.title'),
      description: t('practices.administration.tour.three.description'),
      target: () => refTabPracticeTimes.current,
    },
    {
      title: t('practices.administration.tour.four.title'),
      description: t('practices.administration.tour.four.description'),
      target: () => refDropdownPlans.current,
    },
    {
      title: t('practices.administration.tour.five.title'),
      description: t('practices.administration.tour.five.description'),
      target: () => refSyncButton.current,
    },
  ];

  return (
    <>
      <Button
        type="text"
        className={styles['help-btn']}
        icon={<QuestionCircleOutlined />}
        size="large"
        onClick={() => setTourOpen(true)}
      />
      <Row ref={refCurrentActivePlan}>
        {(isFetchingUpcomingSchedules || !!currentSchedule) && (
          <Col xs={24} md={12}>
            <Spin spinning={isFetchingUpcomingSchedules}>
              <Alert
                icon={<CheckCircleOutlined />}
                message={t(
                  'practices.administration.practice_times.schedules.current.title',
                  { name: currentSchedule?.name }
                )}
                description={scheduleDescriptionText(currentSchedule)}
                type="success"
                showIcon
                style={{ margin: 8 }}
              />
            </Spin>
          </Col>
        )}
        {(isFetchingUpcomingSchedules || !!upcomingSchedule) && (
          <Col xs={24} md={12}>
            <Spin spinning={isFetchingUpcomingSchedules}>
              <Alert
                icon={<RightCircleOutlined />}
                message={t(
                  'practices.administration.practice_times.schedules.upcoming.title',
                  { name: upcomingSchedule?.name }
                )}
                description={scheduleDescriptionText(upcomingSchedule)}
                type="info"
                showIcon
                style={{ margin: 8 }}
              />
            </Spin>
          </Col>
        )}
        {(isFetchingUpcomingSchedules ||
          (!!additionalSchedules && additionalSchedules?.length > 0)) && (
          <Col xs={24}>
            <Collapse
              style={{ margin: 8 }}
              items={[
                {
                  key: '1',
                  label: t(
                    'practices.administration.practice_times.schedules.upcoming.more'
                  ),
                  children: additionalSchedules?.map((schedule) => (
                    <Typography.Paragraph key={schedule.id}>
                      {schedule.name} - {scheduleDescriptionText(schedule)}
                    </Typography.Paragraph>
                  )),
                },
              ]}
              bordered={false}
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col xs={24}>
          <Tabs
            defaultActiveKey="times_overview"
            tabBarExtraContent={
              <Space>
                <div ref={refSyncButton}>
                  <SyncPracticesButton />
                </div>
                <div ref={refDropdownPlans}>
                  <Select
                    style={{ width: 250 }}
                    defaultValue={0}
                    options={schedulesSelect}
                    value={activeScheduleId ?? undefined}
                    loading={isLoadingSchedules}
                    onSelect={(value) => {
                      dispatch(
                        BasketballPracticeTimesSlice.actions.setActiveScheduleId(
                          value
                        )
                      );
                    }}
                  />
                </div>
              </Space>
            }
            items={[
              {
                key: 'times_overview',
                label: (
                  <span ref={refTabPracticeTimes}>
                    {t('practices.administration.practice_times.title')}
                  </span>
                ),
                children: (
                  <PracticeTimesScheduleOverview
                    data={scheduleData}
                    loading={isLoadingSchedules || isLoadingSchedule}
                    activeScheduleId={activeScheduleId ?? 0}
                    disableEditing={false}
                    isDeleting={isDeletingDraftTime}
                    onDelete={(time) => deleteDraftTime(time)}
                    onOpenSettings={openPracticeTimeSettings}
                  />
                ),
              },
              /*{
                key: 'calendar',
                label: t(
                  'practices.administration.practice_times.calendar.title'
                ),
                children: (
                  <PracticeTimesCalendar
                    data={scheduleData}
                    loading={isLoadingSchedule}
                    activeScheduleId={activeScheduleId ?? 0}
                  />
                ),
              },*/
              {
                key: 'schedules',
                label: (
                  <span ref={refTabSchedules}>
                    {t(
                      'practices.administration.practice_times.schedules.title'
                    )}
                  </span>
                ),
                children: (
                  <PracticeTimesSchedulesComponent
                    schedules={schedules ?? []}
                    loading={isLoadingSchedules}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <Tour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
        onFinish={() => markTourAsRead()}
      />
    </>
  );
}
